import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { toast } from 'react-hot-toast';
import { auth } from '@/services/firebase';
import useContactDeveloper from '../../../hooks/useContactDeveloper';

export type SupportTicketDialogVariant = 'user' | 'admin';

type SupportTicketDialogProps = {
  open: boolean;
  onClose: () => void;
  variant?: SupportTicketDialogVariant;
  role?: string | null;
  moduleHint?: string;
};

type SupportTicketFormState = {
  type: 'bug' | 'support' | 'suggestion';
  title: string;
  description: string;
  stepsToReproduce: string;
  expectedResult: string;
  actualResult: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  module: string;
  contactEmail: string;
  allowTechnicalContext: boolean;
};

const TITLE_MIN_LENGTH = 3;
const DESCRIPTION_MIN_LENGTH = 20;

function buildInitialState(
  variant: SupportTicketDialogVariant,
  moduleHint?: string,
  currentUserEmail?: string | null
): SupportTicketFormState {
  return {
    type: variant === 'admin' ? 'bug' : 'support',
    title: '',
    description: '',
    stepsToReproduce: '',
    expectedResult: '',
    actualResult: '',
    severity: 'medium',
    module: variant === 'admin' ? (moduleHint || 'backoffice') : (moduleHint || ''),
    contactEmail: currentUserEmail || '',
    allowTechnicalContext: true,
  };
}

export default function SupportTicketDialog({
  open,
  onClose,
  variant = 'user',
  role = null,
  moduleHint,
}: SupportTicketDialogProps) {
  const submitTicket = useContactDeveloper();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const submitLockRef = useRef(false);
  const isMountedRef = useRef(true);
  const isAuthenticated = Boolean(auth.currentUser);
  const [formData, setFormData] = useState<SupportTicketFormState>(
    buildInitialState(variant, moduleHint, auth.currentUser?.email)
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [descriptionTouched, setDescriptionTouched] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (open) {
      submitLockRef.current = false;
      if (isMountedRef.current) {
        setSubmitting(false);
        setFormData(buildInitialState(variant, moduleHint, auth.currentUser?.email));
        setError('');
        setTitleTouched(false);
        setDescriptionTouched(false);
      }
    }
  }, [open, variant, moduleHint]);

  const isBug = formData.type === 'bug';
  const titleLength = formData.title.trim().length;
  const descriptionLength = formData.description.trim().length;
  const showTitleError = titleTouched && titleLength < TITLE_MIN_LENGTH;
  const showDescriptionError = descriptionTouched && descriptionLength < DESCRIPTION_MIN_LENGTH;
  const canSubmit = isAuthenticated && titleLength >= TITLE_MIN_LENGTH && descriptionLength >= DESCRIPTION_MIN_LENGTH;

  const dialogCopy = useMemo(() => {
    if (variant === 'admin') {
      return {
        title: 'Signaler un incident technique',
        intro: 'Remontez un problème ou une amélioration du back-office à l’équipe Jacana.',
        success: 'Le ticket a bien été enregistré. L’équipe pourra le traiter rapidement.',
      };
    }

    return {
      title: 'Reporter un bug ou contacter l’équipe',
      intro: 'Décrivez le problème ou votre suggestion. Votre message sera enregistré pour traitement.',
      success: 'Votre signalement a bien été enregistré. Merci pour votre retour.',
    };
  }, [variant]);

  const handleFieldChange = (field: keyof SupportTicketFormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.type === 'checkbox'
      ? (event.target as HTMLInputElement).checked
      : event.target.value;

    if (field === 'title') {
      setTitleTouched(true);
    }

    if (field === 'description') {
      setDescriptionTouched(true);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClose = () => {
    if (submitting) return;
    setError('');
    submitLockRef.current = false;
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (submitLockRef.current || submitting) {
      return;
    }

    if (!isAuthenticated) {
      setError('Vous devez être connecté pour envoyer un signalement.');
      return;
    }

    if (!canSubmit) {
      setTitleTouched(true);
      setDescriptionTouched(true);
      return;
    }

    setError('');
    submitLockRef.current = true;
    setSubmitting(true);

    try {
      await submitTicket(formData, {
        moduleHint: formData.module || moduleHint,
      });

      toast.success(dialogCopy.success);
      onClose();
    } catch (submitError: any) {
      if (isMountedRef.current) {
        setError(submitError?.message || 'Impossible d’envoyer le signalement pour le moment. Réessayez dans quelques instants.');
      }
    } finally {
      submitLockRef.current = false;
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
        },
      }}
    >
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack spacing={0.75}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {dialogCopy.title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {dialogCopy.intro}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {!isAuthenticated ? (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                Vous devez être connecté pour envoyer un signalement.
              </Alert>
            ) : null}

            {error ? <Alert severity="error">{error}</Alert> : null}

            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Merci de ne pas inclure d’informations sensibles. Le contexte technique utile est envoyé de manière limitée.
            </Alert>

            <TextField
              select
              fullWidth
              label="Type de demande"
              value={formData.type}
              onChange={handleFieldChange('type')}
            >
              <MenuItem value="bug">Reporter un bug</MenuItem>
              <MenuItem value="support">Demande de support</MenuItem>
              <MenuItem value="suggestion">Suggestion / amélioration</MenuItem>
            </TextField>

            <TextField
              fullWidth
              required
              autoFocus
              label="Titre"
              value={formData.title}
              onChange={handleFieldChange('title')}
              error={showTitleError}
              helperText={
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                  }}
                >
                  <Box component="span">
                    {showTitleError
                      ? `Merci de préciser un titre un peu plus explicite (minimum ${TITLE_MIN_LENGTH} caractères)`
                      : `Minimum ${TITLE_MIN_LENGTH} caractères requis`}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      flexShrink: 0,
                      fontWeight: 600,
                      color: titleLength >= TITLE_MIN_LENGTH
                        ? 'success.main'
                        : showTitleError
                          ? 'error.main'
                          : 'text.secondary',
                    }}
                  >
                    {`${titleLength} / ${TITLE_MIN_LENGTH} caractères`}
                  </Box>
                </Box>
              }
              inputProps={{ maxLength: 120 }}
              placeholder={variant === 'admin' ? 'Ex. Incident paiement côté admin' : 'Ex. Je ne peux pas confirmer ma réservation'}
            />

            <TextField
              fullWidth
              required
              multiline
              minRows={4}
              label="Description"
              value={formData.description}
              onChange={handleFieldChange('description')}
              error={showDescriptionError}
              helperText={
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                  }}
                >
                  <Box component="span">
                    {showDescriptionError
                      ? `Merci de fournir un peu plus de détails (minimum ${DESCRIPTION_MIN_LENGTH} caractères)`
                      : `Minimum ${DESCRIPTION_MIN_LENGTH} caractères requis`}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      flexShrink: 0,
                      fontWeight: 600,
                      color: descriptionLength >= DESCRIPTION_MIN_LENGTH
                        ? 'success.main'
                        : showDescriptionError
                          ? 'error.main'
                          : 'text.secondary',
                    }}
                  >
                    {`${descriptionLength} / ${DESCRIPTION_MIN_LENGTH} caractères`}
                  </Box>
                </Box>
              }
              inputProps={{ maxLength: 4000 }}
              placeholder="Décrivez brièvement ce qui se passe ou votre suggestion."
            />

            {isBug ? (
              <>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Étapes de reproduction"
                  value={formData.stepsToReproduce}
                  onChange={handleFieldChange('stepsToReproduce')}
                  inputProps={{ maxLength: 3000 }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Résultat attendu"
                  value={formData.expectedResult}
                  onChange={handleFieldChange('expectedResult')}
                  inputProps={{ maxLength: 1500 }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Résultat observé"
                  value={formData.actualResult}
                  onChange={handleFieldChange('actualResult')}
                  inputProps={{ maxLength: 1500 }}
                />
                <TextField
                  select
                  fullWidth
                  label="Gravité"
                  value={formData.severity}
                  onChange={handleFieldChange('severity')}
                >
                  <MenuItem value="low">Faible</MenuItem>
                  <MenuItem value="medium">Moyenne</MenuItem>
                  <MenuItem value="high">Élevée</MenuItem>
                  <MenuItem value="critical">Critique</MenuItem>
                </TextField>
              </>
            ) : null}

            {variant === 'admin' ? (
              <TextField
                fullWidth
                label="Module concerné"
                value={formData.module}
                onChange={handleFieldChange('module')}
                inputProps={{ maxLength: 80 }}
                placeholder="Ex. admin-dashboard, réservations, paiement"
              />
            ) : null}

            <TextField
              fullWidth
              label="Email de contact (optionnel)"
              type="email"
              value={formData.contactEmail}
              onChange={handleFieldChange('contactEmail')}
              inputProps={{ maxLength: 254 }}
              placeholder="vous@example.com"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.allowTechnicalContext}
                  onChange={handleFieldChange('allowTechnicalContext')}
                />
              }
              label="J’accepte de partager un contexte technique limité pour aider au diagnostic."
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={submitting} color="inherit">
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit || submitting}
            startIcon={<SendRoundedIcon />}
          >
            {submitting ? 'Envoi…' : 'Envoyer'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
